import { FormData } from "@/components/AdvisorWizard";

export interface Recommendation {
  id: string;
  title: string;
  match: number;
  description: string;
  advantages: string[];
  disadvantages: string[];
  nextSteps: string;
  isRecommended?: boolean;
}

export function getRecommendations(formData: FormData): Recommendation[] {
  const recommendations: Recommendation[] = [];

  // 1. Rückmiete IMMER als Top-Empfehlung hinzufügen (beste Lösung)
  recommendations.push(createRueckmiete(formData));

  // 2. Leibrente-Empfehlung
  if (leibrenteKriterien(formData)) {
    recommendations.push(createLeibrente(formData));
  }

  // 3. Teilverkauf-Empfehlung
  if (teilverkaufKriterien(formData)) {
    recommendations.push(createTeilverkauf(formData));
  }

  // 4. Wohnungsrecht-Empfehlung
  if (wohnungsrechtKriterien(formData)) {
    recommendations.push(createWohnungsrecht(formData));
  }

  // 5. Nießbrauch-Empfehlung
  if (niessbrauchKriterien(formData)) {
    recommendations.push(createNiessbrauch(formData));
  }

  // 6. Umkehrhypothek-Empfehlung
  if (umkehrhypothekKriterien(formData)) {
    recommendations.push(createUmkehrhypothek(formData));
  }

  // Sortierung nach Übereinstimmungsgrad
  const sortedRecommendations = recommendations.sort((a, b) => b.match - a.match);
  
  // WICHTIG: Rückmiete immer als beste Empfehlung markieren
  const rueckmietIndex = sortedRecommendations.findIndex(r => r.id === 'rueckmiete');
  if (rueckmietIndex !== -1) {
    // Rückmiete an erste Stelle setzen
    const rueckmiete = sortedRecommendations.splice(rueckmietIndex, 1)[0];
    rueckmiete.isRecommended = true;
    sortedRecommendations.unshift(rueckmiete);
  } else if (sortedRecommendations.length > 0) {
    sortedRecommendations[0].isRecommended = true;
  }

  return sortedRecommendations;
}

// Kriterien-Funktionen

function leibrenteKriterien(formData: FormData): boolean {
  return (
    (formData.age === '76-80' || formData.age === '81+') &&
    formData.priority === 'Sicherheit' &&
    formData.paymentForm === 'Monatliche Rente' &&
    formData.futurePlans === 'Lebenslang in der Immobilie bleiben'
  );
}

function teilverkaufKriterien(formData: FormData): boolean {
  return (
    formData.priority === 'Flexibilität' ||
    formData.monthlyPayments === 'Ja' ||
    (formData.paymentForm === 'Kombination' && formData.estimatedValue !== 'Unter 200k€')
  );
}

function wohnungsrechtKriterien(formData: FormData): boolean {
  return (
    formData.priority === 'Einfachheit' &&
    formData.paymentForm === 'Einmalzahlung' &&
    formData.monthlyPayments === 'Nein'
  );
}

function niessbrauchKriterien(formData: FormData): boolean {
  return (
    formData.futurePlans === 'Eventuell ins Pflegeheim' ||
    formData.futurePlans === 'Zu Familie ziehen' ||
    formData.mainReason === 'Familie unterstützen'
  );
}

function rueckmietKriterien(formData: FormData): boolean {
  return (
    formData.age === '60-65' || 
    formData.age === '66-70' ||
    formData.paymentForm === 'Einmalzahlung' &&
    formData.monthlyPayments === 'Ja'
  );
}

function umkehrhypothekKriterien(formData: FormData): boolean {
  return (
    formData.mainReason === 'Renovierung finanzieren' ||
    (formData.financialSituation === 'Schwierig' && formData.estimatedValue !== 'Unter 200k€')
  );
}

// Empfehlungs-Erstellungs-Funktionen

function createLeibrente(formData: FormData): Recommendation {
  let match = 95;
  
  // Anpassung basierend auf anderen Faktoren
  if (formData.financialSituation === 'Schwierig') match += 5;
  if (formData.familyStatus === 'Single') match += 3;
  
  return {
    id: 'leibrente',
    title: 'Leibrente',
    match: Math.min(match, 100),
    description: 'Perfekt für Sie: Regelmäßige monatliche Zahlungen bis zum Lebensende bei mietfreiem Wohnen in Ihrer Immobilie.',
    advantages: [
      'Lebenslange finanzielle Sicherheit',
      'Mietfreies Wohnen bis zum Lebensende',
      'Planbare monatliche Einnahmen',
      'Keine Instandhaltungskosten mehr',
      'Schutz vor Inflation durch Wertsteigerungsklauseln'
    ],
    disadvantages: [
      'Kaufpreis liegt unter dem Marktwert',
      'Immobilie kann nicht mehr vererbt werden',
      'Bindung an einen Anbieter'
    ],
    nextSteps: 'Wir empfehlen Ihnen ein persönliches Beratungsgespräch zur Leibrente. Unsere Experten berechnen Ihre individuelle Rentenhöhe.'
  };
}

function createTeilverkauf(formData: FormData): Recommendation {
  let match = 85;
  
  if (formData.priority === 'Flexibilität') match += 10;
  if (formData.estimatedValue === 'Über 800k€') match += 5;
  
  return {
    id: 'teilverkauf',
    title: 'Teilverkauf',
    match: Math.min(match, 100),
    description: 'Ideal für flexible Lösungen: Verkaufen Sie nur den Teil Ihrer Immobilie, den Sie wirklich benötigen.',
    advantages: [
      'Flexibler Kapitalbedarf - Sie bestimmen den Anteil',
      'Rückkaufsrecht für verkaufte Anteile möglich',
      'Behalten alle Rechte an der Immobilie',
      'Vermietung und Verkauf weiterhin möglich',
      'Erbschaft bleibt teilweise erhalten'
    ],
    disadvantages: [
      'Monatliche Nutzungsentgelte für verkaufte Anteile',
      'Komplexere Vertragsstruktur',
      'Wertsteigerung wird geteilt'
    ],
    nextSteps: 'Lassen Sie uns gemeinsam berechnen, welcher Verkaufsanteil optimal für Ihre Bedürfnisse ist.'
  };
}

function createWohnungsrecht(formData: FormData): Recommendation {
  let match = 80;
  
  if (formData.priority === 'Einfachheit') match += 10;
  if (formData.monthlyPayments === 'Nein') match += 5;
  
  return {
    id: 'wohnungsrecht',
    title: 'Verkauf mit Wohnungsrecht',
    match: Math.min(match, 100),
    description: 'Einfach und unkompliziert: Erhalten Sie eine Einmalzahlung und wohnen mietfrei bis zum Lebensende.',
    advantages: [
      'Einfache und übersichtliche Struktur',
      'Keine monatlichen Belastungen',
      'Sofortige Liquidität durch Einmalzahlung',
      'Mietfreies Wohnen garantiert',
      'Keine Instandhaltungskosten'
    ],
    disadvantages: [
      'Kaufpreis liegt unter dem vollen Marktwert',
      'Keine Vermietung der Immobilie möglich',
      'Immobilie kann nicht vererbt werden'
    ],
    nextSteps: 'Wir zeigen Ihnen, wie hoch Ihre Einmalzahlung bei einem Verkauf mit Wohnungsrecht sein könnte.'
  };
}

function createNiessbrauch(formData: FormData): Recommendation {
  let match = 75;
  
  if (formData.futurePlans === 'Eventuell ins Pflegeheim') match += 10;
  if (formData.mainReason === 'Familie unterstützen') match += 8;
  
  return {
    id: 'niessbrauch',
    title: 'Nießbrauch',
    match: Math.min(match, 100),
    description: 'Zukunftssicher: Behalten Sie alle Nutzungsrechte und können die Immobilie sogar vermieten, falls Sie ausziehen.',
    advantages: [
      'Vermietung nach Auszug möglich',
      'Umfassende Nutzungsrechte',
      'Flexibilität bei Lebensveränderungen',
      'Steuervorteile bei Schenkung zu Lebzeiten',
      'Erbschaftsplanung optimiert'
    ],
    disadvantages: [
      'Schwieriger zu vermarkten als andere Optionen',
      'Instandhaltung bleibt beim Verkäufer',
      'Kaufpreis oft niedriger'
    ],
    nextSteps: 'Wir finden spezialisierte Investoren für nießbrauchsbelastete Immobilien und optimieren Ihre Steuervorteile.'
  };
}

function createRueckmiete(formData: FormData): Recommendation {
  // Hoher Basis-Match-Wert - Rückmiete ist im Zweifelsfall immer die beste Lösung
  let match = 95;
  
  if (formData.age === '60-65' || formData.age === '66-70') match += 5;
  if (formData.paymentForm === 'Einmalzahlung') match += 3;
  if (formData.priority === 'Flexibilität') match += 2;
  
  return {
    id: 'rueckmiete',
    title: 'Verkauf mit Rückmiete',
    match: Math.min(match, 100),
    description: 'Unsere Top-Empfehlung: Der Verkauf mit Rückmiete bietet Ihnen maximale Liquidität bei voller Sicherheit. Sprechen Sie unbedingt mit unseren RentePlusImmobilie Experten, um den besten Weg für Ihre individuelle Situation zu finden.',
    advantages: [
      'Kein Mindestalter erforderlich',
      'Sofortige komplette Liquidität zum vollen Marktwert',
      'Volle Mieterrechte und gesetzlicher Mieterschutz',
      'Keine Instandhaltungskosten mehr',
      'Planbare monatliche Kosten',
      'Ideal für jedes Alter und jede Lebenssituation'
    ],
    disadvantages: [
      'Monatliche Mietzahlungen erforderlich',
      'Schuldrechtliche Absicherung'
    ],
    nextSteps: 'Sprechen Sie jetzt mit unseren RentePlusImmobilie Experten! Wir analysieren Ihre individuelle Situation, berechnen Ihre optimale Miete und finden den besten Weg für Sie. Eine persönliche Beratung ist unerlässlich, um die richtige Entscheidung zu treffen.'
  };
}

function createUmkehrhypothek(formData: FormData): Recommendation {
  let match = 65;
  
  if (formData.mainReason === 'Renovierung finanzieren') match += 20;
  if (formData.estimatedValue === 'Über 800k€') match += 10;
  
  return {
    id: 'umkehrhypothek',
    title: 'Umkehrhypothek',
    match: Math.min(match, 100),
    description: 'Flexible Finanzierung: Erhalten Sie einen Kredit gegen Ihre Immobilie ohne monatliche Rückzahlung.',
    advantages: [
      'Flexible Auszahlung nach Bedarf',
      'Keine Tilgung zu Lebzeiten erforderlich',
      'Eigentum bleibt vollständig erhalten',
      'Erben können Kredit übernehmen',
      'Ideal für temporären Liquiditätsbedarf'
    ],
    disadvantages: [
      'Zinsen laufen weiter auf',
      'Begrenzter Kreditrahmen',
      'Immobilie dient als Sicherheit',
      'Nicht bei allen Banken verfügbar'
    ],
    nextSteps: 'Wir prüfen Ihre Bonität und finden spezialisierte Anbieter für Umkehrhypotheken in Ihrer Region.'
  };
}

function createIndividuelleBeratung(): Recommendation {
  return {
    id: 'individuelle-beratung',
    title: 'Persönliche Expertenberatung',
    match: 90,
    description: 'Ihre Situation ist einzigartig. Unsere RentePlusImmobilie Experten empfehlen in den meisten Fällen den Verkauf mit Rückmiete als beste Lösung. Sprechen Sie mit uns, um den optimalen Weg für Sie zu finden.',
    advantages: [
      'Maßgeschneiderte Lösung für Ihre Situation',
      'Persönliche Betreuung durch RentePlusImmobilie Experten',
      'Verkauf mit Rückmiete als bewährte Standardlösung',
      'Alle Optionen werden detailliert geprüft',
      'Berücksichtigung aller individuellen Faktoren',
      'Kostenlose und unverbindliche Erstberatung'
    ],
    disadvantages: [
      'Erfordert detaillierte persönliche Analyse'
    ],
    nextSteps: 'Vereinbaren Sie jetzt einen kostenlosen Beratungstermin mit unseren RentePlusImmobilie Experten. Wir zeigen Ihnen, warum der Verkauf mit Rückmiete oft die beste Lösung ist und analysieren Ihre individuelle Situation im Detail.'
  };
}